import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import {
  dexTradeKeys,
  DexTradeService,
} from 'api/services/httpServices/DexTradeService';
import { positionStatesStatuses } from '../../../trade-monitor/usePageState';
import {
  GetDexOpenPositionsParams,
  GetDexOpenPositionsResponseCursor,
  OpenPositionListItem,
} from 'api/types/httpsTypes/dex-trade';
import { useOpenTradesSearchFilters } from './usePageParams';
import { useInView } from 'react-intersection-observer';
import { forwardRef, useEffect, useMemo, useState } from 'react';
import { usePositionUpdate } from 'api/hooks/usePositionUpdate';
import { updatePositionsList } from '../../../trade-monitor/components/updatePositionWebsocket';
import { useGlobalChain } from 'modules/routing/chainProvider';
import { useUserState } from 'modules/user/UserContext';
import Button from 'modules/shared-components/button/button';
import { Link } from 'react-router-dom';
import PageTemplate from '../../../components/layout';
import { useToggle } from 'modules/utils/useToggle';
import { useChainInfo } from 'api/hooks/useChainInfo';
import { desktopOpenTradesColumns } from './columns';
import { TokenColumn } from './columnsComponents/tokenColumn';
import { OpenTradesFiltersBar } from './OpenTradesFilters';
import { ClosePosition } from '../../../trade-monitor/ClosePosition';
import {
  EmptyPageDex,
  ErrorPageDex,
} from '../../../research/components/shared/Common';
import { twMerge } from 'tailwind-merge';
import Table from 'modules/shared-components/data-display/table';
import Header from 'modules/shared-components/data-display/table/header';
import { TableRow } from './TableRow';
import { getAuthLoginUrl } from '../../../../auth/utils';
import { FullScreenModal } from '../../../components/modal/FullScreenModal';
import { PositionDetails } from '../../../trade-monitor/components/Details/PositionDetails';

const LIMIT = 10;

export function OpenTradesComponent({ isTerminal }: { isTerminal?: boolean }) {
  const { filtersState } = useOpenTradesSearchFilters();

  const { user } = useUserState();
  const [selectedChain] = useGlobalChain();
  const queryClient = useQueryClient();
  const updatedPosition = usePositionUpdate(selectedChain);

  const params: GetDexOpenPositionsParams = useMemo(
    () => ({
      sort: filtersState.sort,
      status: positionStatesStatuses.active,
      chain: selectedChain,
      botId: filtersState.botId,
      tokenAddress: filtersState.tokenAddress,
      vaultId: filtersState.vaultId ? [filtersState.vaultId] : undefined,
    }),
    [
      filtersState.sort,
      selectedChain,
      filtersState.botId,
      filtersState.tokenAddress,
      filtersState.vaultId,
    ]
  );

  const {
    data,
    error,
    isLoading,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    isRefetching,
  } = useInfiniteQuery({
    queryKey: dexTradeKeys.getPositionsV3(params),
    queryFn: ({ pageParam = undefined }) => {
      return DexTradeService.getPositionsV3({
        ...params,
        limit: LIMIT,
        cursor: pageParam,
      });
    },
    getNextPageParam: (lastPage) => {
      const next = lastPage?.pagination?.next;
      return next ?? undefined;
    },
    enabled: !!user,
    staleTime: 0,
    cacheTime: 0,
  });

  // Flatten all pages into single array
  const items = useMemo(() => {
    return data?.pages.flatMap((page) => page.data) ?? [];
  }, [data]);

  // Get pagination info from the last page
  const pagination = useMemo(() => {
    if (!data?.pages.length) {
      return { limit: LIMIT, next: null };
    }

    const lastPage = data?.pages[data.pages.length - 1];
    return {
      limit: LIMIT,
      next: lastPage?.pagination?.next ?? null,
    };
  }, [data]);

  // Infinite scroll trigger
  const { ref } = useInView({
    onChange: (inView) => {
      if (inView && hasNextPage && !isFetchingNextPage) {
        void fetchNextPage();
      }
    },
    threshold: 0,
    rootMargin: '100px',
  });

  const allVisibleIds = new Set(
    data?.pages.flatMap((page: any) => page.data.map((p: any) => p.id))
  );

  // WebSocket position updates
  useEffect(() => {
    if (!updatedPosition) return;

    const existingInAnyPage = allVisibleIds.has(updatedPosition.p.id);

    queryClient.setQueryData(
      dexTradeKeys.getPositionsV3(params),
      (oldData: any) => {
        if (!oldData) return oldData;

        let dataChanged = false;

        const updatedPages = oldData.pages.map((page: any, index: number) => {
          const { updatedItems, wasAdded, wasRemoved } = updatePositionsList(
            page.data,
            updatedPosition,
            true,
            params.tokenAddress,
            params.botId,
            {
              allowAdd: index === 0 && !existingInAnyPage,
              allowRemove: true,
            }
          );

          if (wasAdded || wasRemoved || updatedItems !== page.data) {
            dataChanged = true;
            return {
              ...page,
              data: updatedItems, // new reference
            };
          }

          return page; // unchanged reference
        });

        // ✅ Only return new data if something actually changed
        if (!dataChanged) return oldData;

        return {
          ...oldData,
          pages: updatedPages,
        };
      }
    );
  }, [updatedPosition, queryClient, params]);

  const page = { pagination, data: items };

  if (!user) {
    return (
      <PageTemplate>
        <div className="flex flex-col justify-center items-center py-24 xxs:mx-4 space-y-2">
          <div className="text-sm font-bold">Login and start trading.</div>
          <Button
            variant="primary"
            as={Link}
            to={getAuthLoginUrl()}
            className=" xxs:px-12 "
            type="button"
          >
            LOGIN
          </Button>
        </div>
      </PageTemplate>
    );
  }

  return (
    <TradesComponent
      isTerminal={isTerminal}
      hasNextPage={!!hasNextPage}
      ref={ref}
      page={page}
      error={error as boolean | undefined}
      isLoading={isLoading || isRefetching}
    />
  );
}

export function thDynamicClass(s: string) {
  return s === '' ? 'col-span-1' : '';
}

interface Props {
  hasNextPage: boolean;
  page: GetDexOpenPositionsResponseCursor | undefined;
  error: boolean | undefined;
  isLoading: boolean;
  isTerminal?: boolean;
}

export const TradesComponent = forwardRef<HTMLDivElement, Props>(
  ({ hasNextPage, page, error, isLoading, isTerminal }, ref) => {
    const [selectedTokens, setSelectedTokens] = useState<string[]>([]); // Explicit type
    const [openCloseAllModal, { open, close }] = useToggle();
    const [selectedChain] = useGlobalChain();
    const chainAssetPrice = useChainInfo(selectedChain)?.price;
    const [openPositionId, setOpenPositionId] = useState<string | null>(null);

    const trades = page?.data ?? [];

    const columns = useMemo(() => {
      const baseColumns = desktopOpenTradesColumns({
        chainAssetPrice,
        isTerminal,
      });

      const tokenColumn = TokenColumn({
        trades,
        selectedTokens,
        setSelectedTokens,
      });

      return [tokenColumn, ...baseColumns];
    }, [
      chainAssetPrice,
      selectedTokens,
      setSelectedTokens,
      trades,
      isTerminal,
    ]);

    return (
      <div className="space-y-2">
        <div className="flex space-x-1 items-center">
          <div
            className={
              isTerminal
                ? 'xxs:p-2 flex justify-between items-center w-full'
                : 'xxs:pl-2'
            }
          >
            <div className="flex xxs:space-x-1 items-center">
              {!isTerminal && <OpenTradesFiltersBar isTerminal={isTerminal} />}
              {selectedTokens && selectedTokens.length >= 1 && (
                <div>
                  <Button
                    type="button"
                    onClick={open}
                    className="xxs:m-0 xxs:mx-2 xxs:px-2 xxs:py-1"
                    variant="errorDex"
                  >
                    <span className="normal-case">
                      Bulk Sell ({selectedTokens.length})
                    </span>
                  </Button>
                  {openCloseAllModal &&
                    page?.data &&
                    page?.data?.length >= 1 && (
                      <ClosePosition
                        setSelectedTokens={setSelectedTokens}
                        position={page.data.filter(
                          (trade: OpenPositionListItem) =>
                            selectedTokens &&
                            selectedTokens.length >= 1 &&
                            selectedTokens.includes(trade.id)
                        )}
                        onCancel={close}
                      />
                    )}
                </div>
              )}
            </div>
          </div>
        </div>

        {error ? (
          <ErrorPageDex />
        ) : (
          <div className="overflow-x-auto">
            <div
              className={twMerge(
                !isTerminal && 'lg:min-w-[1300px] ',
                'w-full xxs:space-y-1'
              )}
            >
              <Table className="xxs:[&_td:first-of-type:not(.details)]:pl-2 xxs:[&_th:first-of-type]:pl-2 lg:[&_td:first-of-type:not(.details)]:pl-6 lg:[&_th:first-of-type]:pl-6">
                <Header
                  trClassName={twMerge(
                    isTerminal
                      ? 'grid-cols-[250px_1fr_1fr_1fr_1fr_1fr_100px]'
                      : 'grid-cols-[250px_1fr_1fr_1fr_1fr_1fr_1fr_1fr_1fr_100px]',
                    'xxs:dark:bg-dex-black-900 xxs:grid'
                  )}
                  columns={columns}
                  thDynamicClass={thDynamicClass}
                  rowClassName="xxs:px-2 xxs:py-1"
                  className="xxs:text-xs lg:grid xxs:dark:bg-dex-black-900"
                />
                <tbody className="lg:block lg:h-terminal overflow-y-auto">
                  {isLoading ? (
                    [...Array(10).keys()]
                      .map((i) => i + 1)
                      .map((index) => (
                        <Table.LoadingRow
                          thDynamicClass={thDynamicClass}
                          key={index}
                          elementClassName="xxs:dark:bg-dex-black-700"
                          className={twMerge(
                            isTerminal
                              ? 'lg:grid-cols-[250px_1fr_1fr_1fr_1fr_1fr_100px]'
                              : 'lg:grid-cols-[250px_1fr_1fr_1fr_1fr_1fr_1fr_1fr_1fr_100px]',
                            'lg:grid xxs:dark:bg-dex-black-900'
                          )}
                          columns={columns}
                        />
                      ))
                  ) : page?.data?.length === 0 ? (
                    <EmptyPageDex height="100px" title="No Trades Found" />
                  ) : (
                    <>
                      {page?.data?.map((item) => (
                        <TableRow
                          setSelectedTokens={setSelectedTokens}
                          selectedTokens={selectedTokens}
                          isTerminal={isTerminal}
                          isOpen={true}
                          key={item.id}
                          onOpen={() => setOpenPositionId(item.id)}
                          data={item}
                          columnsDef={columns}
                        />
                      ))}
                      {hasNextPage && (
                        <>
                          <Table.LoadingRow
                            cellClassName="xxs:py-3"
                            elementClassName="xxs:dark:bg-dex-black-700"
                            className={twMerge(
                              isTerminal
                                ? 'lg:grid-cols-[250px_1fr_1fr_1fr_1fr_1fr_100px]'
                                : 'lg:grid-cols-[250px_1fr_1fr_1fr_1fr_1fr_1fr_1fr_1fr_100px]',
                              'lg:grid xxs:dark:bg-dex-black-900'
                            )}
                            columns={columns}
                          />

                          <tr key={page?.pagination.next || page?.data.length}>
                            <td colSpan={columns.length}>
                              <div ref={ref} style={{ height: 1 }} />
                            </td>
                          </tr>
                        </>
                      )}
                    </>
                  )}
                </tbody>
              </Table>
              {openPositionId && (
                <FullScreenModal
                  hideHeader
                  className="xxs:p-0 lg:p-0 xxs:pb-16"
                  close={() => setOpenPositionId(null)}
                >
                  <PositionDetails id={openPositionId} />
                </FullScreenModal>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }
);
